import { useState } from 'react';
import { haptic, cloudStorage } from '../lib/telegram';

const ONBOARDING_KEY = 'vibe_onboarding_done';

const SLIDES = [
  {
    icon: '🚀',
    title: 'Покажи что навайбкодил',
    subtitle: 'Добавь проект и найди первых пользователей, партнёров или инвесторов',
    gradient: 'linear-gradient(135deg, #7850ff, #a855f7)',
  },
  {
    icon: '🔍',
    title: 'Найди нужного человека',
    subtitle: 'Размести заявку — и тебя найдут разработчики, готовые воплотить идею',
    gradient: 'linear-gradient(135deg, #a855f7, #ec4899)',
  },
  {
    icon: '🤝',
    title: 'Объединяй усилия',
    subtitle: 'Маркетплейс, где vibe-кодеры и бизнес находят друг друга',
    gradient: 'linear-gradient(135deg, #ec4899, #7850ff)',
  },
] as const;

interface OnboardingProps {
  onComplete: () => void;
}

export function Onboarding({ onComplete }: OnboardingProps) {
  const [current, setCurrent] = useState(0);

  const markDone = () => {
    // Persist in localStorage (fallback) and Telegram CloudStorage
    try { localStorage.setItem(ONBOARDING_KEY, '1'); } catch { /* ignore */ }
    cloudStorage.set(ONBOARDING_KEY, '1').catch(() => {/* ignore */});
    onComplete();
  };

  const next = () => {
    haptic.light();
    if (current < SLIDES.length - 1) {
      setCurrent((c) => c + 1);
    } else {
      haptic.success();
      markDone();
    }
  };

  const slide = SLIDES[current];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      background: '#07070f',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 28px',
    }}>
      {/* Skip — always visible at top right (Phase 3) */}
      <button
        type="button"
        onClick={() => {
          haptic.selection();
          markDone();
        }}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,0.4)',
          fontSize: '0.8125rem',
          cursor: 'pointer',
          padding: '8px 12px',
        }}
      >
        Пропустить
      </button>

      {/* Animated icon */}
      <div
        className="animate-float"
        style={{
          position: 'relative',
          width: 120,
          height: 120,
          borderRadius: 32,
          background: slide.gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 56,
          marginBottom: 32,
          transition: 'background 0.5s ease',
        }}
      >
        {slide.icon}
        {/* Glow */}
        <div style={{
          position: 'absolute',
          inset: -8,
          borderRadius: 40,
          background: slide.gradient,
          filter: 'blur(24px)',
          opacity: 0.35,
          zIndex: -1,
        }} />
      </div>

      <h2 style={{
        fontSize: '1.375rem',
        fontWeight: 800,
        color: '#fff',
        textAlign: 'center',
        marginBottom: 12,
        fontFamily: 'Unbounded, sans-serif',
        letterSpacing: '-0.01em',
      }}>
        {slide.title}
      </h2>

      <p style={{
        fontSize: '0.9375rem',
        color: 'rgba(255,255,255,0.5)',
        textAlign: 'center',
        lineHeight: 1.6,
        maxWidth: 280,
        marginBottom: 40,
      }}>
        {slide.subtitle}
      </p>

      {/* Dot indicators */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
        {SLIDES.map((_, i) => (
          <div
            key={i}
            style={{
              height: 6,
              width: i === current ? 28 : 6,
              borderRadius: 3,
              background: i === current ? '#7850ff' : 'rgba(255,255,255,0.15)',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </div>

      {/* CTA button */}
      <button
        type="button"
        className="btn btn-primary animate-pulse-glow"
        onClick={next}
        style={{
          width: '100%',
          maxWidth: 300,
          padding: '15px 24px',
          borderRadius: 16,
          fontSize: '1rem',
          fontWeight: 700,
        }}
      >
        {current < SLIDES.length - 1 ? 'Далее →' : 'Начать! 🚀'}
      </button>
    </div>
  );
}

/** Check if onboarding should be shown (first visit). Async — uses CloudStorage if available. */
export async function shouldShowOnboarding(): Promise<boolean> {
  const cloudValue = await cloudStorage.get(ONBOARDING_KEY);
  if (cloudValue) return false;
  try {
    if (localStorage.getItem(ONBOARDING_KEY)) return false;
  } catch { /* ignore */ }
  return true;
}
